import React, { useState, useRef, useEffect } from "react";
import { Sparkles } from "lucide-react";

/**
 * Small "Auto-calculated" indicator + tooltip. Hover / click to see the
 * derivation. Used on every field the Manual ZBC form fills in for the user.
 *
 * Props:
 *   reason      – human-readable derivation string (line breaks with \n)
 *   overridden  – when true, the icon dims + tooltip says "you have overridden"
 *   onReset     – optional callback; when provided, tooltip shows a "Reset"
 *                 link that restores the auto value
 */
export default function AutoTip({ reason, overridden = false, onReset, className = "" }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    if (!reason) return null;

    return (
        <span
            ref={wrapRef}
            className={`relative inline-flex items-center align-middle ml-1 ${className}`}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
                className={[
                    "inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors",
                    overridden
                        ? "text-slate-300 hover:text-slate-400"
                        : "text-[hsl(var(--copper))] hover:text-[hsl(var(--copper))]/80",
                ].join(" ")}
                title={overridden ? "You overrode this auto-calculated value" : "Auto-calculated — hover for details"}
                data-testid="auto-tip-icon"
                aria-label="Auto-calculated field"
            >
                <Sparkles className="w-3 h-3" />
            </button>

            {open && (
                <span
                    role="tooltip"
                    className="absolute z-50 left-0 top-full mt-1 min-w-[240px] max-w-[340px] rounded-md border border-slate-300 bg-white shadow-lg p-2.5 text-[11px] text-slate-700 leading-relaxed"
                    style={{ whiteSpace: "pre-line" }}
                    data-testid="auto-tip-tooltip"
                >
                    <div className="flex items-center gap-1 mb-1 text-[hsl(var(--copper))] font-semibold tracking-wide uppercase text-[10px]">
                        <Sparkles className="w-2.5 h-2.5" />
                        {overridden ? "Auto-calc (overridden)" : "Auto-calculated"}
                    </div>
                    <div className="font-mono text-[10.5px] whitespace-pre-line">{reason}</div>
                    {overridden && (
                        <div className="mt-1.5 text-[10px] text-amber-700">You have entered a manual value. The auto-calc is paused.</div>
                    )}
                    {onReset && overridden && (
                        <button
                            type="button"
                            onClick={onReset}
                            className="mt-2 text-[10.5px] text-[hsl(var(--copper))] hover:underline font-medium"
                            data-testid="auto-tip-reset"
                        >
                            ↻ Reset to auto value
                        </button>
                    )}
                </span>
            )}
        </span>
    );
}
